import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, History, Loader2, Package, Search } from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { farmGet, farmPatch, ALERT_SCORTA_LABELS } from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function AlertBadges({ alerts }) {
  if (!alerts?.length) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {alerts.map((a) => {
        const meta = ALERT_SCORTA_LABELS[a] || { label: a, className: "bg-slate-50 text-slate-600 border-slate-200" };
        return (
          <Badge key={a} variant="outline" className={`text-[10px] ${meta.className}`}>
            {meta.label}
          </Badge>
        );
      })}
    </div>
  );
}

function QtyCell({ value, meta, label }) {
  return (
    <div className="text-right">
      <div className="font-semibold text-slate-800">{value ?? "—"}</div>
      <div className="text-[10px] text-slate-400" title={label}>
        {meta || label}
      </div>
    </div>
  );
}

function EditRow({ row, busy, onSave }) {
  const [dich, setDich] = useState(row.qty_dichiarata ?? "");
  const [ver, setVer] = useState(row.qty_verificata ?? "");
  const [note, setNote] = useState(row.note || "");

  return (
    <tr className="border-t border-emerald-50 bg-emerald-50/40">
      <td colSpan={11} className="px-3 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Dichiarata (struttura)</label>
            <Input
              type="number"
              min={0}
              className="mt-1 h-8 w-24"
              value={dich}
              onChange={(e) => setDich(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Verificata (farmacia)</label>
            <Input
              type="number"
              min={0}
              className="mt-1 h-8 w-24"
              value={ver}
              onChange={(e) => setVer(e.target.value)}
            />
          </div>
          <div className="min-w-[180px] flex-1">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Note</label>
            <Input
              className="mt-1 h-8"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Opzionale"
            />
          </div>
          <Button
            size="sm"
            disabled={busy}
            onClick={() => {
              const body = { note: note.trim() || undefined };
              if (dich === "" || dich === null) body.clear_dichiarata = true;
              else body.qty_dichiarata = Number(dich);
              if (ver === "" || ver === null) body.clear_verificata = true;
              else body.qty_verificata = Number(ver);
              onSave(body);
            }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default function ScorteFarmacia() {
  const { token } = useFarmaciaAuth();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [strutture, setStrutture] = useState([]);
  const [orgFilter, setOrgFilter] = useState("");
  const [alertFilter, setAlertFilter] = useState(() => searchParams.get("alert") || "");
  const [fascicoloFilter, setFascicoloFilter] = useState(
    () => searchParams.get("fascicolo_id") || ""
  );
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [soglia, setSoglia] = useState(7);
  const [editId, setEditId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [histId, setHistId] = useState(null);
  const [hist, setHist] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => {
    const fromUrl = searchParams.get("alert") || "";
    setAlertFilter(fromUrl);
    setFascicoloFilter(searchParams.get("fascicolo_id") || "");
  }, [searchParams]);

  useEffect(() => {
    if (!token) return;
    farmGet("/farmacia/strutture", token, { lite: true })
      .then((str) => setStrutture(str.items || []))
      .catch(() => setStrutture([]));
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const scorte = await farmGet("/farmacia/scorte", token, {
        ...(orgFilter ? { organization_id: orgFilter } : {}),
        ...(alertFilter ? { alert: alertFilter } : {}),
        ...(fascicoloFilter ? { fascicolo_id: fascicoloFilter } : {}),
      });
      setItems(scorte.items || []);
      if (scorte.soglia_bassa != null) setSoglia(scorte.soglia_bassa);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento scorte");
    } finally {
      setLoading(false);
    }
  }, [token, orgFilter, alertFilter, fascicoloFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return items;
    return items.filter((row) => {
      const blob = [
        row.ospite_nome,
        row.farmaco,
        row.principio_attivo,
        row.struttura_nome,
        row.codice_fiscale,
        ...(row.alerts || []),
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return blob.includes(ql);
    });
  }, [items, q]);

  const saveRow = async (id, body) => {
    setBusyId(id);
    try {
      const res = await farmPatch(`/farmacia/scorte/${encodeURIComponent(id)}`, token, body);
      toast.success("Scorta aggiornata");
      setItems((prev) => prev.map((r) => (r.id === id ? res.item : r)));
      setEditId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setBusyId(null);
    }
  };

  const openHist = async (id) => {
    if (histId === id) {
      setHistId(null);
      return;
    }
    setHistId(id);
    setHistLoading(true);
    try {
      const res = await farmGet(`/farmacia/scorte/${encodeURIComponent(id)}/rilevazioni`, token);
      setHist(res.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Storico non disponibile");
      setHist([]);
    } finally {
      setHistLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <FarmaciaPageChrome
        subtitle={`Multi-fonte per ospite/prodotto: Carepy, dichiarato struttura, verificato farmacia, ordinato e consegnato. Alert semplici (soglia bassa ≤ ${soglia}).`}
      />
      {fascicoloFilter ? (
        <div className="flex items-center justify-between rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800">
          <span>Scorte filtrate per l’assistito selezionato.</span>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => setFascicoloFilter("")}>
            Mostra tutte
          </Button>
        </div>
      ) : null}
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="border-b px-4 py-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Package className="h-4 w-4 text-emerald-600" />
            Inventario corrente
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Cerca ospite, farmaco, struttura…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-400" />
              <select
                className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)}
              >
                <option value="">Tutte le strutture</option>
                {strutture.map((s) => (
                  <option key={s.organization_id} value={s.organization_id}>
                    {s.nome}
                  </option>
                ))}
              </select>
              <select
                className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                value={alertFilter}
                onChange={(e) => setAlertFilter(e.target.value)}
              >
                <option value="">Tutti gli alert</option>
                {Object.entries(ALERT_SCORTA_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-500">
              Nessuna scorta. Carica un Carepy con unità residue o registra una verifica.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-100">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Struttura / Ospite</th>
                    <th className="px-3 py-2 font-medium">Prodotto</th>
                    <th className="px-3 py-2 font-medium text-right">Carepy</th>
                    <th className="px-3 py-2 font-medium text-right">Dichiarata</th>
                    <th className="px-3 py-2 font-medium text-right">Verificata</th>
                    <th className="px-3 py-2 font-medium text-right">Ordinata</th>
                    <th className="px-3 py-2 font-medium text-right">In consegna</th>
                    <th className="px-3 py-2 font-medium text-right">Consegnata</th>
                    <th className="px-3 py-2 font-medium text-right">Copertura</th>
                    <th className="px-3 py-2 font-medium">Alert</th>
                    <th className="px-3 py-2 font-medium">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <Fragment key={row.id || `${row.fascicolo_id}-${row.farmaco}`}>
                      <tr className="border-t border-slate-100 align-top">
                        <td className="px-3 py-2">
                          <div className="text-xs text-slate-500">{row.struttura_nome || "—"}</div>
                          <div className="font-medium text-slate-800">{row.ospite_nome || "—"}</div>
                          {row.codice_fiscale ? (
                            <div className="text-[10px] text-slate-400">{row.codice_fiscale}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-slate-800">{row.farmaco}</div>
                          <div className="text-xs text-slate-500">{row.principio_attivo || "—"}</div>
                        </td>
                        <td className="px-3 py-2">
                          <QtyCell
                            value={row.unita_carepy ?? row.unita_residue}
                            meta={formatWhen(row.carepy_at)}
                            label="Carepy"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <QtyCell
                            value={row.qty_dichiarata}
                            meta={
                              row.dichiarata_at
                                ? `${formatWhen(row.dichiarata_at)}${row.dichiarata_by_nome ? ` · ${row.dichiarata_by_nome}` : ""}`
                                : "struttura"
                            }
                            label="Dichiarata"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <QtyCell
                            value={row.qty_verificata}
                            meta={
                              row.verificata_at
                                ? `${formatWhen(row.verificata_at)}${row.verificata_by_nome ? ` · ${row.verificata_by_nome}` : ""}`
                                : "farmacia"
                            }
                            label="Verificata"
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-700">
                          {row.qty_ordinata ?? 0}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-700">
                          {row.qty_in_consegna ?? 0}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-700">
                          {row.qty_consegnata ?? 0}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {row.giorni_copertura != null ? (
                            <div>
                              <span className={`font-semibold ${row.giorni_copertura < 7 ? "text-rose-700" : row.giorni_copertura < 14 ? "text-amber-700" : "text-emerald-700"}`}>
                                {row.giorni_copertura} gg
                              </span>
                              <p className="text-[10px] text-slate-400">{row.unita_giornaliere} u/gg</p>
                            </div>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2">
                          <AlertBadges alerts={row.alerts} />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => setEditId(editId === row.id ? null : row.id)}
                            >
                              Modifica
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-slate-500"
                              onClick={() => openHist(row.id)}
                            >
                              <History className="mr-1 h-3 w-3" />
                              Storico
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {editId === row.id ? (
                        <EditRow
                          row={row}
                          busy={busyId === row.id}
                          onSave={(body) => saveRow(row.id, body)}
                        />
                      ) : null}
                      {histId === row.id ? (
                        <tr className="border-t border-slate-50 bg-slate-50/60">
                          <td colSpan={10} className="px-3 py-3">
                            {histLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                            ) : hist.length === 0 ? (
                              <p className="text-xs text-slate-500">Nessuna rilevazione registrata.</p>
                            ) : (
                              <ul className="space-y-1 text-xs text-slate-600">
                                {hist.map((h) => (
                                  <li key={h.id} className="flex flex-wrap gap-x-3">
                                    <span className="font-medium text-slate-800">{h.campo}</span>
                                    <span>valore: {h.valore ?? "—"}</span>
                                    <span>fonte: {h.fonte}</span>
                                    <span>{h.autore_nome || "—"}</span>
                                    <span className="text-slate-400">{formatWhen(h.created_at)}</span>
                                    {h.note ? <span className="text-slate-400">{h.note}</span> : null}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
